import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import { EVENT_INVITATION } from '@web/src/features/event/event-attendee-signup-dialog/event-attendee-signup-dialog'
import EventAttendeesHeader from '@web/src/features/event/event-attendees-header/event-attendees-header'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { capitalizeFirstLetter } from '@web/src/lib/formatters'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { UserPlusIcon, UserSearchIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EventAttendeesInvitePageQuery,
  EventAttendeesInvitePageQueryVariables,
  EventAttendeesPageQuery,
  EventInvitationCancelMutation,
  EventInvitationCancelMutationVariables,
  EventInviteSortEnum,
} from 'types/graphql'
import { z } from 'zod'
import { EVENT_QUERY } from './$eventIdOrSlug_.attendees'

export const QUERY: TypedDocumentNode<EventAttendeesInvitePageQuery, EventAttendeesInvitePageQueryVariables> = gql`
  query EventAttendeesInvitePageQuery($filter: EventInviteSearchFilter!, $page: Int, $sortBy: EventInviteSortEnum) {
    eventInviteSearchPage(filter: $filter, page: $page, sortBy: $sortBy) {
      entries {
        profile {
          ...ProfileBasicInfo
        }
        attendanceStatus
        joinRequestStatus
        invitationId
        inviter {
          id
        }
      }
      count
      perPage
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const EVENT_INVITATION_CANCEL: TypedDocumentNode<
  EventInvitationCancelMutation,
  EventInvitationCancelMutationVariables
> = gql`
  mutation EventInvitationCancelMutation($id: String!) {
    eventInvitationCancel(id: $id)
  }
`

const EVENT_INVITE_PAGE_SIZE = 50

type EventInviteSearchInput = {
  page?: number
  name?: string
  sortBy?: string
}

type EventInviteLoaderDeps = {
  page: number
  name?: string
  sortBy: string
}

function resolveEventInviteSearchParams(search: EventInviteSearchInput): EventInviteLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'NAME',
  }
}

function eventInviteQueryVariables(
  eventIdOrSlug: string,
  deps: EventInviteLoaderDeps
): EventAttendeesInvitePageQueryVariables {
  return {
    page: deps.page,
    sortBy: deps.sortBy as EventInviteSortEnum,
    filter: {
      eventId: eventIdOrSlug,
      name: deps.name,
      excludeSelf: false,
      maxResults: EVENT_INVITE_PAGE_SIZE,
    },
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/event/$eventIdOrSlug_/invite')({
  component: EventAttendeesInvitePage,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'NAME',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Invite Event Attendees | ${env.VITE_APP_NAME}`,
      description: 'Invite Attendees to Event',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loaderDeps: ({ search }) => resolveEventInviteSearchParams(search),
  loader: async ({ context, deps, params }) => {
    const resolvedDeps = deps as EventInviteLoaderDeps
    const variables = eventInviteQueryVariables(params.eventIdOrSlug, resolvedDeps)

    const [{ queryRef: eventQueryRef }, { queryRef: inviteQueryRef }] = await Promise.all([
      queryAndPreload(context, EVENT_QUERY, {
        variables: {
          eventIdOrSlug: params.eventIdOrSlug,
        },
      }),
      queryAndPreload(context, QUERY, { variables }),
    ])

    return { eventQueryRef, inviteQueryRef }
  },
})

function EventAttendeesInvitePage() {
  const { t } = useTranslation()
  const { eventIdOrSlug } = Route.useParams()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveEventInviteSearchParams(search), [search])
  const client = useApolloClient()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const formRef = useRef(null)
  const [cancelModal, setCancelModal] = useState(false)
  const [invitationToCancel, setInvitationToCancel] = useState(null)

  const { eventQueryRef, inviteQueryRef } = Route.useLoaderData()
  const { data: eventData } = useReadQuery(eventQueryRef as QueryRef<EventAttendeesPageQuery>)
  const { data, error } = useReadQuery(inviteQueryRef as QueryRef<EventAttendeesInvitePageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formMethods = useForm({
    defaultValues: {
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      sortBy: resolvedSearch.sortBy,
    },
  })

  const [eventInvitation, { loading: inviting }] = useMutation(EVENT_INVITATION, {
    onCompleted: () => {
      client.refetchQueries({ include: [QUERY] })
    },
    onError: (err) => {
      toast.error(t('Error sending invitation: {{message}}', { message: err.message }))
    },
  })

  const [eventInvitationCancel, { loading: canceling }] = useMutation(EVENT_INVITATION_CANCEL, {
    onCompleted: () => {
      toast.success(t('Invitation cancelled successfully'))
      client.refetchQueries({ include: [QUERY] })
      setCancelModal(false)
      setInvitationToCancel(null)
    },
    onError: (err) => {
      toast.error(t('Error cancelling invitation: {{message}}', { message: err.message }))
      setCancelModal(false)
      setInvitationToCancel(null)
    },
  })

  const filterResults = useCallback(() => {
    const fieldData = formMethods.getValues()
    navigate({
      search: {
        page: fieldData.page,
        name: fieldData.name || undefined,
        sortBy: fieldData.sortBy,
      },
      replace: true,
      viewTransition: false,
    } as never)
  }, [formMethods, navigate])

  const handleLiveFilter = useCallback(() => {
    filterResults()
  }, [filterResults])

  const handleResetFilters = () => {
    formMethods.reset({
      page: 1,
      name: '',
      sortBy: 'NAME',
    })

    navigate({
      search: {
        page: 1,
        name: undefined,
        sortBy: 'NAME',
      },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handlePageChange = useCallback(
    (nextPage) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value || undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleInvite = (profileId: string) => {
    eventInvitation({
      variables: {
        eventIdOrSlug: eventIdOrSlug,
        profileId: profileId,
      },
    })
  }

  const handleCancelInvite = (entry) => {
    setInvitationToCancel({
      id: entry.invitationId,
      profileName: entry.profile.name,
    })
    setCancelModal(true)
  }

  const confirmCancelInvite = () => {
    if (invitationToCancel) {
      eventInvitationCancel({
        variables: {
          id: invitationToCancel.id,
        },
      })
    }
  }

  const pageData = useMemo(
    () => data?.eventInviteSearchPage || { entries: [], count: 0, perPage: 50 },
    [data?.eventInviteSearchPage]
  )
  const currentPage = resolvedSearch.page

  // Check if current user can cancel a specific invitation
  const canCancelInvitation = (entry) => {
    if (entry?.joinRequestStatus === 'PENDING') {
      if (eventData?.event?.canEdit) return true

      // Regular attendees can only cancel their own invitations
      return entry.inviter?.id === currentUser?.profile?.id
    }

    return false
  }

  const getStatusDisplay = (entry) => {
    if (entry.attendanceStatus === 'NONE') {
      return (
        <div className="flex max-w-full flex-nowrap items-center gap-1">
          <span className="whitespace-nowrap">{t('Invited')}</span>
        </div>
      )
    }

    if (entry.attendanceStatus) {
      return (
        <div className="flex max-w-full flex-nowrap items-center gap-1">
          <span className="whitespace-nowrap">{t(capitalizeFirstLetter(entry.attendanceStatus.toLowerCase()))}</span>
        </div>
      )
    }

    if (entry.joinRequestStatus === 'PENDING') {
      const canCancel = canCancelInvitation(entry)
      return (
        <div className="flex max-w-full flex-nowrap items-center gap-2">
          <span className="whitespace-nowrap">{t('Pending...')}</span>
          {canCancel && (
            <Button
              disabled={!canCancel || canceling}
              onClick={() => handleCancelInvite(entry)}
              size="sm"
              type="button"
              variant="secondary"
            >
              {canceling ? <Spinner className="size-4" /> : null}
              {t('Cancel')}
            </Button>
          )}
        </div>
      )
    }

    return (
      <Button disabled={inviting} onClick={() => handleInvite(entry.profile.id)} size="sm" type="button">
        {inviting ? <Spinner className="size-4" /> : <UserPlusIcon className="size-4" />}
        {t('Invite')}
      </Button>
    )
  }

  if (error) {
    return (
      <>
        <ErrorAlert error={error} />
      </>
    )
  }

  return (
    <>
      <div className="mx-auto w-full max-w-4xl p-2">
        <div className="flex w-full flex-col items-stretch gap-4">
          <EventAttendeesHeader activeTab={'invite'} event={eventData?.event} />

          <Form className="w-full" formMethods={formMethods} ref={formRef}>
            <div className="flex w-full flex-col items-stretch gap-4">
              {/* Search and Filters */}
              <SearchExpand
                clearSearchLabel={t('Clear search')}
                inputProps={{ 'aria-label': t('Search profiles') }}
                loading={isLoading}
                minChars={0}
                name="name"
                onSearchChange={handleSearchChange}
                placeholder={t('Search profiles by name...')}
                toggleFiltersLabel={t('Toggle filters')}
              >
                <Card className="gap-0 py-0">
                  <CardContent className="px-3 py-3">
                    <div className="flex w-full flex-col gap-4">
                      <div className="flex w-full flex-wrap items-start gap-4">
                        <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleLiveFilter}>
                          <option value="NEWEST">{t('Newest')}</option>
                          <option value="OLDEST">{t('Oldest')}</option>
                          <option value="NAME">{t('Name')}</option>
                        </NativeSelectFormField>
                      </div>

                      <div className="flex w-full justify-end">
                        <Button onClick={handleResetFilters} type="button" variant="secondary">
                          {t('Reset')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </SearchExpand>

              {/* Results */}
              {pageData.entries.length > 0 && (
                <div className="flex w-full flex-col gap-2">
                  {/* Profile List */}
                  {pageData.entries.map((entry) => (
                    <Card
                      className={cn(
                        'w-full cursor-pointer border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1',
                        entry.attendanceStatus && 'opacity-40'
                      )}
                      key={entry.profile.id}
                      size="sm"
                    >
                      <CardContent className="px-2 py-1">
                        <div className="flex items-center justify-between gap-2">
                          <SFProfileBanner
                            className="cursor-pointer"
                            onClick={() =>
                              navigate({
                                to: '/profile/$profileId/about',
                                params: { profileId: entry.profile.id },
                              } as never)
                            }
                            profile={entry.profile}
                            size="xl"
                          />

                          {getStatusDisplay(entry)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <PaginationControl
                    count={pageData.count}
                    onPageChange={handlePageChange}
                    page={currentPage}
                    pageSize={pageData.perPage}
                  />
                </div>
              )}

              {pageData.entries.length === 0 && (
                <Empty className="py-20">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <UserSearchIcon />
                    </EmptyMedia>
                    <EmptyTitle>{t('No profiles found')}</EmptyTitle>
                    {resolvedSearch.name && (
                      <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
                    )}
                  </EmptyHeader>
                </Empty>
              )}
            </div>
          </Form>
        </div>

        {/* Cancel Invitation Modal */}
        <ConfirmDialog
          cancelText={t('No')}
          confirmText={t('Yes')}
          loading={canceling}
          onConfirm={confirmCancelInvite}
          onOpenChange={(e) => {
            setCancelModal(e.open)
            if (!e.open) {
              setInvitationToCancel(null)
            }
          }}
          open={cancelModal}
          text={t('Are you sure you want to cancel the invitation for {{name}}?', {
            name: invitationToCancel?.profileName,
          })}
          title={t('Cancel Invitation')}
          variant="basic"
        />
      </div>
    </>
  )
}
